import { isPlatformBrowser } from '@angular/common';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { Observable } from 'rxjs';
import { debugMaxEntries } from 'src/app/consts';
import { parseServerTiming } from 'src/app/lib/parse-server-timing';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { StateService } from 'src/app/services/state.service';
import { StorageService } from 'src/app/services/storage.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private state: StateService,
    private storage: StorageService,
    private featureFlags: FeatureFlagsService,
    @Optional() @Inject('ERROR_WRAPPER') private errorWrapper: { error: Error },
    @Inject(PLATFORM_ID) private platformId: string
  ) {}

  // intercept function
  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // returning an observable to complete the request cycle
    return new Observable(observer => {
      next.handle(req).subscribe(
        (res: HttpResponse<unknown>) => {
          if (res instanceof HttpResponse) {
            if (this.featureFlags.get('serverTiming') && res.headers.get('Server-Timing')?.length) {
              this.state.serverTimingData.push({
                ...parseServerTiming(res.headers.get('Server-Timing') || ''),
                url: req.url,
                requestBody: req.body ? JSON.stringify(req.body, null, 2) : '',
                method: req.method,
                responseTime: Date.now(),
              });
              if (this.state.serverTimingData.length > 100) {
                this.state.serverTimingData.shift();
              }
            }

            if (this.state.recordDebug) {
              if (this.state.debugResponseCache.length > debugMaxEntries) this.state.debugResponseCache.shift();
              this.state.debugResponseCache.push({ ...res, __time: Date.now(), __type: 'response' });
            }
            observer.next(res);
            observer.complete();
          }
        },
        (err: HttpErrorResponse) => {
          if (this.state.recordDebug) {
            if (this.state.debugResponseCache.length > debugMaxEntries) this.state.debugResponseCache.shift();
            this.state.debugResponseCache.push({ ...err, __time: Date.now(), __type: 'errorResponse' });
          }

          if (err.status === 0) {
            console.debug('got a status 0 error', err);
          } else if (err?.error?.code) {
            const code = err.error.code;

            if (err.status === 503 && code === 'maintenance_exception') {
              if (!req.url.includes('ignore_503_error')) {
                if (isPlatformBrowser(this.platformId)) {
                  const returnUrl = location.href.replace(location.origin, '');
                  location.href = `${location.origin}/maintenance?returnUrl=${encodeURIComponent(returnUrl)}`;
                  return;
                }
                if (this.errorWrapper) {
                  this.errorWrapper.error = err;
                }
              }
            }

            if (req.url.includes('ignore_errors')) {
              return;
            }

            if (code === 'visitor_exists') {
              return;
            }

            if (code === 'password_invalid' || code === 'password_required') {
              if (!req.url.includes('ignore_401_error')) {
                if (isPlatformBrowser(this.platformId)) {
                  this.state.removeAllGalleryPasswords();
                  // eslint-disable-next-line no-debugger
                  debugger;
                  location.reload();
                  return;
                }
              }
            } else if (
              code === 'any_authentication_required' ||
              code === 'session_expired' ||
              code === 'forbidden_request'
            ) {
              if (!req.url.includes('ignore_401_error')) {
                this.storage.remove('guest-auth');
                this.storage.removeCookie('guest-auth');
                this.state.guestAuthToken = undefined;
                this.state.guest = undefined;
              }
            } else {
              console.debug('got an error with an error code', req.url, err.error);
            }
          } else {
            if (!req.url.includes('api.storyblok.com')) {
              console.debug('got an error without an error code', err);
            }
          }
          observer.error(err);
        }
      );
    });
  }
}
